import { useMutation, useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { terrainsByIdsAdapter, createAdapter } from 'adapters/orderAdacter';
import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { convertToNumber, currency, noCurrency } from 'helpers/utils';
import { projectService, ordersService, offersService } from 'services';
import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import isBetween from 'dayjs/plugin/isBetween';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useParams, useSearchParams } from 'react-router-dom';
import useErrors from 'hooks/useErrors';
import { Button, Form } from 'react-bootstrap';
import { contextApp } from 'context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SoftBadge from 'components/common/SoftBadge';

dayjs.extend(minMax);
dayjs.extend(isBetween);

const IndeterminateCheckbox = forwardRef(({ indeterminate, ...rest }, ref) => {
 const defaultRef = useRef();

 const resolvedRef = ref || defaultRef;

 useEffect(() => {
  // console.log(indeterminate, 'se esta actualizando', rest);
  resolvedRef.current.indeterminate = indeterminate;
 }, [resolvedRef, indeterminate]);

 return <Form.Check.Input className="ms-1" type="checkbox" ref={resolvedRef} {...rest} />;
});

export const useOrderHook = () => {
 const { auth } = contextApp();
 const { setError } = useErrors();
 const [params] = useSearchParams();
 const { company: companyID, projectId } = useParams();
 const query = params.get('products') ?? null;
 const items = query ? query.split(',') : [];
 const [temVals, setTemVals] = useState({});
 const [loading, setLoading] = useState(false);
 const [products, setProducts] = useState([]);
 const [skipPageReset, setSkipPageReset] = useState(false);
 const [discounts, setDiscounts] = useState([]);
 const [editableRowIndex, setEditableRowIndex] = useState({
  currentIndex: null,
  data: null
 });

 const agentVirtual = { firstName: 'Agente', lastName: 'Virtual', docNumber: companyID, phone: 'sin info' };
 /* eslint-disable react/prop-types */
 const columns = useMemo(
  () => [
   {
    id: 'selection',
    Header: ({ getToggleAllRowsSelectedProps }) => (
     <div className="">
      <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
     </div>
    ),
    headerProps: {
     className: 'p-1',
     style: {
      maxWidth: 30
     }
    },
    cellProps: {
     style: {
      maxWidth: 30
     }
    },
    Cell: ({ row }) => (
     <div>
      <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
     </div>
    )
   },
   {
    accessor: 'blocks',
    Header: 'Bloque',
    maxWidth: 50,
    Cell: ({ value }) => <h6 className="mb-0 text-primary fs--1">{value}</h6>
   },
   {
    accessor: 'stage',
    Header: 'Etapa',
    maxWidth: 50,
    Cell: ({ value }) => <h6 className="mb-0 text-primary fs--1">{value}</h6>
   },
   {
    accessor: 'num',
    Header: 'Lote',
    Cell: ({ value }) => <h6 className="mb-0 text-primary fs--1">{value}</h6>
   },
   {
    accessor: 'area',
    Header: 'Area',
    cellProps: { style: { minWidth: '100px' } },
    Cell: ({ value }) => (
     <h6 className="text-700 fs--2 m-0">
      <FontAwesomeIcon icon="check" className="text-success me-2" />
      {value * 1} mtr2
      <FontAwesomeIcon icon="caret-up" className="ms-1" />
     </h6>
    )
   },
   {
    accessor: 'mtr',
    Header: 'Mtr2',
    Cell: ({ value }) => (
     <SoftBadge pill bg="dark">
      {currency(value, true)}
      <FontAwesomeIcon icon="caret-up" className="ms-1" />
     </SoftBadge>
    )
   },
   {
    accessor: 'price',
    Header: 'Valor',
    cellProps: { style: { minWidth: '100px' } },
    Cell: ({ value }) => (
     <>
      <SoftBadge pill bg="dark">
       {currency(value, true)}
       <FontAwesomeIcon icon="caret-up" className="ms-1" />
      </SoftBadge>

      <div className="end-0 top-50 translate-middle-y hover-actions me-1 me-md-3">
       <Button size="sm" variant="link" type="button" className="d-flex align-items-center fs--1 text-700 p-0">
        <FontAwesomeIcon icon="trash-alt" />
       </Button>
      </div>
     </>
    )
   }
   /* {
    accessor: '_id',
    Header: '...',
    maxWidth: 10,
    Cell: ({ value, rows: [rows] }) => (
     <div className="end-0 top-50 translate-middle-y hover-actions">
      <Button size="sm" variant="link" type="button" className="d-flex align-items-center fs--1 text-700 p-0">
       <FontAwesomeIcon icon="trash-alt" />
      </Button>
     </div>
    )
   } */
  ],
  []
 );
 /* eslint-enable react/prop-types */
 const { getProject } = projectService.useApiProjects();
 const { createOrder } = ordersService.useApiOrders();
 const { getOffers } = offersService.useApiOffers();

 const {
  register,
  handleSubmit,
  formState: { errors },
  watch,
  setValue,
  getValues,
  reset
 } = useForm({
  defaultValues: {
   apply: 0,
   totals: 0,
   saving: 0,
   offers: [],
   initials: 1,
   iniQuota: 0,
   fncQuota: 0,
   financings: 1,
   iniVal: '$ 0',
   fncVal: '$ 0',
   dateDiffs: [],
   notAdmin: true,
   discounts: false,
   minInitialPay: '$ 0',
   company: null,
   receivers: [],
   customers: [],
   agent: auth?.person?._id ?? null,
   business: auth?.company?._id ?? companyID,
   executor: auth?.person ?? agentVirtual,
   emitters: auth?.company ? [{ ...auth?.company, type: 'business' }] : [],
   date: dayjs().format('YYYY-MM-DD'),
   quotasInitial: { 'date-1': dayjs().add(1, 'month').format('YYYY-MM-DD'), 'fee-1': '$ 0' },
   quotasFinan: { 'date-1': dayjs().add(2, 'month').format('YYYY-MM-DD'), 'fee-1': '$ 0' }
  }
 });

 const { mutate: newOrder } = useMutation(createOrder);

 useQuery(['offers'], () => getOffers({ project: projectId }), {
  onSuccess: data => setValue('offers', data),
  enabled: !!projectId
 });

 const { isLoading, isError, error, data } = useQuery({
  queryKey: ['terrainsByIds'],
  queryFn: () => getProject(projectId),
  onSuccess: data => setValue('defaultValues', terrainsByIdsAdapter(data, items)),
  onSettled: () => console.log('termino la carga en useOrderHook'),
  onError: err => setError(err),
  enabled: !!projectId
  //staleTime: Infinity
 });

 const updateMyData = (terrains, changesFound, removedProducts, addProducts) => {
  setSkipPageReset(true);
  // console.log({ terrains, changesFound, removedProducts, addProducts });

  if (!products.length) return setProducts(terrains);

  return setProducts(old =>
   old
    .filter(row => !removedProducts.some(e => e == row._id))
    .map(row => {
     const rowChanges = changesFound.find(e => e._id === row._id) ?? {};
     return { ...row, ...rowChanges };
    })
    .concat(addProducts)
  );
 };

 const changedProducts = () => {
  const changesFound = [];
  const removedProducts = [];
  const addProducts = [];
  const defaultValues = watch('defaultValues') || { terrains: [], allTerrains: [] };
  const terrains = defaultValues.terrains.length ? defaultValues.terrains : defaultValues.allTerrains;

  // console.log(terrains, 'terrains cambios detectados');

  const withoutChanges = terrains.every(terrain => {
   const oldTerrain = products.find(e => e._id === terrain._id) ?? {};
   if (!oldTerrain?._id) addProducts.push(terrain);
   const isSame = terrain.status === oldTerrain?.status || terrain.price === oldTerrain?.price;
   //console.log({ isSame, terrain, oldTerrain }, !oldTerrain?._id);
   if (!isSame) changesFound.push(terrain);
   return isSame;
  });

  if (products.length > terrains.length) {
   products.forEach(product => {
    if (!terrains.some(e => e._id === product._id)) removedProducts.push(product._id);
   });
  }

  if (!withoutChanges || removedProducts.length) {
   //console.log({ changesFound, removedProducts, addProducts });
   return updateMyData(terrains, changesFound, removedProducts, addProducts);
  }
 };

 const onError = e => {
  console.log(e, 'este es el error que surje');
 };

 const onSubmitData = async data => {
  const contract = await createAdapter({ ...data, discounts });
  console.log('se esta enviando el formulario ', contract);
  setLoading(true);
  newOrder(contract, {
   onSuccess: order => console.log(order),
   onError: err => setError(err),
   onSettled: (data, error) => {
    setLoading(false);
    if (error) toast.error('Error al procesar la solicitud');
    toast.success('Orden creada de forma exitosa');
   }
  });
 };

 const quotas = (name, unfocused = false) => {
  const data = getValues();
  const { initials, financings, tem, notAdmin, defaultValues } = data;
  const iniVal = noCurrency(data.iniVal || 0);
  const fncVal = noCurrency(data.fncVal || 0);
  const dateDiffs = [];

  if (
   (iniVal && /[^\d ]/.test(initials)) ||
   (unfocused && !Number(initials)) ||
   (name !== 'initials' && initials < 1 && iniVal > 0)
  ) {
   if (fncVal && !financings) setValue('financings', 1);
   return setValue('initials', 1);
  }

  const iniQuota = iniVal / initials || 0;
  const minQuotasIni = Math.floor(iniVal / defaultValues.minFeeIni);
  const quotasInitial = {};
  let lastInitialDate;

  const fncQuota = fncVal / financings || 0;
  const minQuotasFnc = Math.floor(fncVal / defaultValues.minFeeFnc);
  const quotasFinan = {};

  const maxMinFeeIni = Math.floor(iniVal / 1000);
  const maxMinFeeFnc = Math.floor(fncVal / 1000);

  if (initials > defaultValues.maxInitials && notAdmin) {
   toast.info('No es posible exceder el maximo de cuotas prestablecidas');
   return setValue('initials', defaultValues.maxInitials);
  } else if (minQuotasIni < initials && notAdmin) {
   toast.info('Se modificara la cantidad de las cuotas para no afectar el monto minimo de cada cuota');
   return setValue('initials', minQuotasIni);
  } else if (maxMinFeeIni < initials) {
   toast.info('Se modificara la cantidad de las cuotas para no afectar el monto minimo que puede tener cada cuota ($ 1,000)');
   return setValue('initials', maxMinFeeIni);
  }

  if (financings > defaultValues.maxFinancing && notAdmin) {
   toast.info('No es posible exceder el maximo de cuotas prestablecidas');
   return setValue('financings', defaultValues.maxFinancing);
  } else if (minQuotasFnc < financings && notAdmin) {
   toast.info('Se modificara la cantidad de las cuotas para no afectar el monto minimo de cada cuota en la financiacion');
   return setValue('financings', minQuotasFnc);
  } else if (maxMinFeeFnc < financings) {
   toast.info('Se modificara la cantidad de las cuotas para no afectar el monto minimo que puede tener cada cuota ($ 1,000)');
   return setValue('financings', maxMinFeeFnc);
  }

  for (let i = 1; i < parseInt(initials) + 1; i++) {
   quotasInitial[`fee-${i}`] = currency(iniQuota, true);
   const beforeDate = quotasInitial[`date-${i - 1}`];
   lastInitialDate = dayjs.max(dayjs(tem?.quotasInitial?.[`date-${i}`]), dayjs(beforeDate).add(1, 'month')).format('YYYY-MM-DD');
   const diff = dayjs(lastInitialDate).diff(beforeDate, 'month');
   if (diff > 1) dateDiffs.push(`quotasInitial.date-${i}`);
   quotasInitial[`date-${i}`] = lastInitialDate;
  }

  for (let i = 1; i < parseInt(financings) + 1; i++) {
   quotasFinan[`fee-${i}`] = currency(fncQuota, true);
   const beforeDate = i === 1 ? lastInitialDate : quotasFinan[`date-${i - 1}`];
   const newDate = dayjs.max(dayjs(tem?.quotasFinan?.[`date-${i}`]), dayjs(beforeDate).add(1, 'month')).format('YYYY-MM-DD');
   const diff = dayjs(newDate).diff(beforeDate, 'month');
   if (diff > 1) dateDiffs.push(`quotasFinan.date-${i}`);
   quotasFinan[`date-${i}`] = newDate;
  }

  reset({ ...data, quotasInitial, iniQuota, quotasFinan, fncQuota, lastInitialDate, dateDiffs });
  Discounts();
 };

 const feeAmount = data => {
  const [type, name] = data.split('.');
  const form = getValues();
  const { initials, iniQuota, financings, fncQuota, defaultValues, notAdmin } = form;
  const isIni = type === 'quotasInitial';
  const val = noCurrency(isIni ? form.iniVal : form.fncVal);
  const quota = isIni ? iniQuota : fncQuota;
  const numDues = isIni ? initials : financings;
  const minFee = isIni ? defaultValues.minFeeIni : defaultValues.minFeeFnc;

  let dues = form[type];
  let quotas = 0;
  const oldFee = currency(quota, true);
  const newVal = noCurrency(form[type][name]);

  const arrayQuotas = Object.entries(form[type])
   .filter(([key]) => key.startsWith('fee'))
   .map(([key, value]) => {
    if (value !== oldFee) return [key, noCurrency(value)];
    quotas++;
    return [key, value];
   });

  const amountAltered = arrayQuotas.filter(([, value]) => value !== oldFee).reduce((a, [, value]) => a + value, 0);

  const newfee = (val - amountAltered) / (quotas || numDues - 1);
  const newFee = currency(newfee, true);

  if ((newfee < minFee && notAdmin) || newfee < 1) {
   const total = quota * quotas + amountAltered;
   const valDiff = newVal - Math.abs(total - val);
   setValue(data, temVals[data] || currency(valDiff, true));
   return toast.info('El cambio que intentas realizar altera el valor de la cuota minina en otros campos diferentes a este');
  }

  if (!quotas) arrayQuotas.map(([key]) => key !== name && (dues = { ...dues, [key]: newFee }));
  else
   arrayQuotas
    .filter(([, value]) => value === oldFee)
    .map(([key, value]) => value === oldFee && (dues = { ...dues, [key]: newFee }));

  setValue(isIni ? 'iniQuota' : 'fncQuota', newfee);
  setValue(type, dues);
  Discounts();
 };

 const updateQuotaDate = data => {
  const [type, name, num] = data.split(/\.|-/);
  const form = getValues();
  const { tem, notAdmin, date, lastInitialDate } = form;
  const isIni = type === 'quotasInitial';
  const departureDate = isIni ? date : lastInitialDate;
  const dateBefore = form[type][`${name}-${parseInt(num) - 1}`] || departureDate;

  let dateDiffs = form.dateDiffs.filter(e => e !== data);
  let newDate = form[type][`${name}-${num}`];
  const isBefore = dayjs(dateBefore).isBefore(newDate, 'day');
  let dates = form[type];

  if (!isBefore) {
   newDate = dayjs(dateBefore).add(1, 'month').format('YYYY-MM-DD');
   return toast.info('No se puede establecer una fecha menor a las anteriores');
  }
  const diff = dayjs(newDate).diff(dateBefore, 'day', true);
  const diffMonths = dayjs(newDate).diff(dateBefore, 'month');
  if (diffMonths > 1) dateDiffs.push(data);

  if (notAdmin && diff > 47 && isIni) {
   setValue(data, dayjs(dateBefore).add(1, 'month').format('YYYY-MM-DD'));
   return toast.info('No se puede establecer una fecha mayor a 45 dias de la separacion');
  }

  dates[`${name}-${num}`] = newDate;
  let dateToCompare = newDate;
  Object.entries(form[type])
   .filter(([key]) => key.startsWith('date') && key.split('-')[1] > parseInt(num))
   .map(([key]) => {
    dateToCompare = dayjs(dateToCompare).add(1, 'month').format('YYYY-MM-DD');
    dates[key] = dateToCompare;
    dateDiffs = dateDiffs.filter(e => e !== `${type}.${key}`);
   });

  setValue('tem', { ...tem, [type]: dates });
  setValue('dateDiffs', dateDiffs);
  setValue(type, dates);
 };

 const mainFieldsChanges = (amount, field, type = null) => {
  const { defaultValues, ...form } = getValues();
  const totals = noCurrency(form.totals);
  const minInitialPay = noCurrency(form.minInitialPay);

  if (type === 'unfocused') {
   const inic = convertToNumber(totals * defaultValues.iniPercentage - minInitialPay);
   const ini = inic < defaultValues.minFeeIni && form.notAdmin ? defaultValues.minFeeIni : inic;
   const newFnc = convertToNumber(totals - ini - minInitialPay - form.saving);

   if (field === 'minInitialPay') {
    if (minInitialPay < defaultValues.minInitialPay && form.notAdmin) {
     setValue('minInitialPay', currency(defaultValues.minInitialPay, true));
     toast.info(`El monto de la separacion no puede ser menor a ${currency(defaultValues.minInitialPay, true)}`);
    }
    setValue('iniVal', currency(ini, true));
    setValue('fncVal', currency(newFnc, true));
   } else if (field === 'iniVal' && amount < ini && form.notAdmin) {
    setValue('iniVal', currency(ini, true));
    setValue('fncVal', currency(newFnc, true));

    toast.info(
     `El monto de la inicial no puede ser menor al ${currency(
      defaultValues.iniPercentage,
      true,
      true
     )} establecido del valor total`
    );
   } else if (field === 'iniVal' && (amount > ini || amount < ini)) {
    setValue('fncVal', currency(convertToNumber(totals - amount - minInitialPay - form.saving), true));
   }

   quotas(field);
  } else {
   const maxInitial = convertToNumber(totals - minInitialPay);

   if (field === 'minInitialPay' && amount > totals) {
    setValue('minInitialPay', currency(totals, true));
    setValue('iniVal', 0);
    setValue('fncVal', 0);
    toast.info('El monto de la separacion puede ser maximo de ' + currency(totals, true));
    return quotas(field);
   }

   if (field === 'iniVal' && amount > maxInitial) {
    setValue('iniVal', currency(maxInitial, true));
    setValue('fncVal', 0);
    toast.info('El monto de la inicial puede ser maximo de ' + currency(maxInitial, true));
    return quotas(field);
   }
  }
  return;
 };

 const verifyFields = ({ keyCode, target: { name, value } }) => {
  const oldVal = temVals[name];
  const { notAdmin, defaultValues } = getValues();
  if (keyCode == 'focus') return setTemVals({ ...temVals, [name]: value });
  if (keyCode == 27 && oldVal !== value) return setValue(name, oldVal);
  if (oldVal === value) return;

  const amount = noCurrency(value);
  const iniFee = name.startsWith('quotasInitial.fee');
  const fncFee = name.startsWith('quotasFinan.fee');

  if (iniFee && amount < defaultValues.minFeeIni && notAdmin) {
   setValue(name, temVals[name] || currency(defaultValues.minFeeIni, true));
   return toast.info(
    'El valor que intenta establecer es menor al monto minimo permitido en una cuota. \nRestableceremos el campo a su valor anterior'
   );
  }

  if (fncFee && amount < defaultValues.minFeeFnc && notAdmin) {
   setValue(name, temVals[name] || currency(defaultValues.minFeeFnc, true));
   return toast.info(
    'El valor que intenta establecer es menor al monto minimo permitido en una cuota. \nRestableceremos el campo a su valor anterior'
   );
  }

  if (name === 'initials') return quotas(name, true);
  if (name === 'minInitialPay') return mainFieldsChanges(amount, name, 'unfocused');
  if (name === 'iniVal') return mainFieldsChanges(amount, name, 'unfocused');
  if (name === 'fncVal') return mainFieldsChanges(amount, name, 'unfocused');
 };

 const proposal = () => {
  const { notAdmin, minInitialPay } = getValues();
  if (notAdmin) {
   const amount = noCurrency(minInitialPay);
   setValue('tem', {});
   return mainFieldsChanges(amount, 'minInitialPay', 'unfocused');
  }
 };

 const Discounts = (up = false) => {
  const {
   offers,
   emitters,
   quotasFinan,
   minInitialPay,
   quotasInitial,
   defaultValues: { totals, company }
  } = getValues();

  const quotas = [
   ...Object.values(quotasInitial).filter(e => e.startsWith('$')),
   ...Object.values(quotasFinan).filter(e => e.startsWith('$'))
  ];

  const minIni = noCurrency(minInitialPay);

  setDiscounts(
   offers.map(e => {
    const searchVal = noCurrency(totals) * e.rate;
    const amount = quotas.reduce((a, b, i) => (i < e.maxQuota ? a + noCurrency(b) : a + 0), minIni);
    const isApply = amount >= searchVal;
    return { ...e, isApply };
   })
  );
  const business = emitters.find(e => e.type === 'business');
  setValue('company', company?._id ?? null);
  setValue('emitters', business?._id ? [business, company] : [company]);
  setValue('totals', currency(totals, true));
  if (up) return mainFieldsChanges(minIni, 'minInitialPay', 'unfocused');
 };

 useEffect(() => {
  const query = params.get('products') ?? null;
  setValue('defaultValues', terrainsByIdsAdapter(data, query ? query.split(',') : []));
 }, [params]);

 useEffect(() => {
  const subscription = watch((value, { name /* , type */ }) => {
   if (name === undefined) return;

   //const arrayData = JSON.parse(localStorage.getItem('order'));
   //localStorage.setItem('order', JSON.stringify({ ...arrayData, ...value }));

   //console.log('watch ', name, value[name]);
   if (name === 'user') return console.log(name, value[name]);
   if (name === 'initials') return quotas(name);
   if (name === 'financings') return quotas(name);
   if (name === 'minInitialPay') return mainFieldsChanges(noCurrency(value.minInitialPay), name);
   if (name === 'iniVal') return mainFieldsChanges(noCurrency(value.iniVal), name);
   if (name === 'saving') return mainFieldsChanges(0, 'minInitialPay', 'unfocused');
   if (name.startsWith('quotasInitial.fee')) return feeAmount(name);
   if (name.startsWith('quotasFinan.fee')) return feeAmount(name);
   if (name.startsWith('quotasInitial.date')) return updateQuotaDate(name);
   if (name.startsWith('quotasFinan.date')) return updateQuotaDate(name);
   if (name === 'notAdmin') return proposal();
   if (name === 'offers') return Discounts();
   if (name === 'defaultValues') {
    changedProducts();
    return Discounts(true);
   }
   //if (name === 'defaultValues') return Discounts(true);
  });
  return () => subscription.unsubscribe();
 }, [watch]);

 return {
  auth,
  watch,
  register,
  handleSubmit,
  errors,
  setValue,
  verifyFields,
  onSubmitData,
  isLoading,
  isError,
  error,
  onError,
  discounts,
  loading,
  data,
  columns,
  products,
  updateMyData,
  skipPageReset,
  editableRowIndex,
  setEditableRowIndex
 };
};

IndeterminateCheckbox.propTypes = {
 indeterminate: PropTypes.bool
};

export default { useOrderHook };
